import {useEffect, useState} from 'react';
import {Button, Segmented} from '@vector';
import {REGISTER_URL} from '../routes';
import {fetchPricing} from '../homeService';
import styles from './PricingPanel.module.css';

/** Cents to "$79.95", the way the rest of the app formats a fee. */
function formatFee(feeCents) {
    return `$${(feeCents / 100).toFixed(2)}`;
}

const AUDIENCE = {SCHOOL: 'SCHOOL', HOME: 'HOME'};
const OPTIONS = [
    {label: 'Schools', value: AUDIENCE.SCHOOL},
    {label: 'At home', value: AUDIENCE.HOME},
];

/**
 * Fork of `sections/Pricing.js` for the redesigned card look. Same `fetchPricing` call and price-
 * availability edge cases; the audience switch moves from a hand-rolled Button radiogroup to
 * vector's new `Segmented` (already a real `role="radiogroup"` of radio inputs under the hood, so
 * the accessible-announcement property the original implementation was built for still holds).
 * `sections/Pricing.js` itself is untouched, since `Home.js` still renders it directly.
 */
export default function PricingPanel({content}) {
    const [audience, setAudience] = useState(AUDIENCE.SCHOOL);
    const [pricing, setPricing] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchPricing()
            .then((data) => !cancelled && setPricing(data))
            .catch(() => !cancelled && setFailed(true));
        return () => {
            cancelled = true;
        };
    }, []);

    const student = pricing?.student;
    const teacher = pricing?.teacher;
    const noStudentPrice = failed || (pricing && !student);

    return (
        <div className={styles.card}>
            <div className={styles.head}>
                <h2 className={styles.heading}>{content.heading}</h2>
                <Segmented
                    size="large"
                    options={OPTIONS}
                    value={audience}
                    onChange={setAudience}
                    aria-label="Who is this for?"/>
            </div>

            <div className={styles.figure}>
                {audience === AUDIENCE.SCHOOL ? (
                    <>
                        <p className={styles.price}>{content.schoolPrice}</p>
                        <p className={styles.priceNote}>{pricing?.schoolPriceNote || content.schoolNote}</p>
                    </>
                ) : (
                    <>
                        {student && (
                            <>
                                <p className={styles.price}>{formatFee(student.feeCents)} AUD</p>
                                <p className={styles.priceNote}>
                                    for a {student.subscriptionMonths}-month individual student subscription
                                </p>
                            </>
                        )}
                        {noStudentPrice && <p className={styles.priceNote}>{content.priceUnavailable}</p>}
                    </>
                )}
            </div>

            <div className={styles.footer}>
                <Button surface="base" size="large" href={REGISTER_URL}>
                    Join now
                </Button>
                {teacher && (
                    <p className={styles.hint}>
                        Teacher accounts are available for {formatFee(teacher.feeCents)}.{' '}
                        <a href={`mailto:${content.contactEmail}`}>Contact us</a> for more information.
                    </p>
                )}
            </div>
        </div>
    );
}
